import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router';
import { applyBranding, applyBrandingFonts, type BrandingPalette } from '@gftg/ui';
import { assetUrl } from '../lib/api';
import { useEvent, type PublicEvent } from '../lib/queries';
import { useSession } from '../lib/session';
import { DesktopNav } from './DesktopNav';

/**
 * What every screen sits in, which is one of two things: the phone column the design draws, framed
 * as a sheet on a desk once the window is wider than a phone, or — from 1024px, for somebody signed
 * in — the app spread out under a header, wide enough for two cards side by side.
 *
 * Branding is applied here rather than per page, because the palette arrives from the database
 * (CNT-04): the theme's own values render first and the organizer's overrides land as soon as the
 * event query resolves, once for the whole app.
 *
 * What frames a screen — the logo header and footer on home, the tab bar everywhere else — is
 * decided by the layout routes, not here.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const event = useEvent();
  const branding = event.data?.branding;
  const { session, isPending } = useSession();
  const { pathname } = useLocation();

  // Sign-in wears the lockup above its form, and two of the same mark side by side reads as a
  // mistake, so the band beside it carries only the words. The questionnaire wears none, and there
  // the band carries the mark. While the session is still resolving, sign-in is what comes next.
  const screenWearsTheLogo = isPending || !session;

  useEffect(() => {
    if (!branding) {
      return;
    }
    applyBranding(branding.palette as BrandingPalette);
    applyBrandingFonts({ display: branding.displayFont, ui: branding.uiFont });
  }, [branding]);

  // Once she is in, a laptop gets the app spread out: the destinations in a header across the top
  // and a column wide enough for two cards beside each other. The two screens that are one task
  // each keep the phone column instead — sign-in has nowhere to navigate to, and the questionnaire
  // has its own way out (PRF-08), so widening either would only put its fields on longer lines.
  const spread = Boolean(session) && pathname !== '/account/questionnaire';

  if (spread) {
    return (
      <div className="flex min-h-dvh flex-col lg:bg-canvas">
        <DesktopNav event={event.data} />

        {/* Below 1024px this is still the framed phone column; above it the frame comes off, the
            canvas fills the window and this is only the width everything lines up to. */}
        <div className={`${COLUMN} lg:max-w-5xl lg:min-h-0 lg:flex-1 lg:border-0 lg:shadow-none`}>
          {children}
        </div>
      </div>
    );
  }

  return (
    // Three columns on a wide screen, the middle one exactly the column's width, so the desk band
    // either side is a grid cell with something in it rather than a margin worked out from `50vw`.
    // Below that the wrapper is an ordinary block and `mx-auto` centres the column as before.
    <div className="xl:grid xl:grid-cols-[1fr_40rem_1fr]">
      <DeskBrand event={event.data} withLogo={!screenWearsTheLogo} />
      <div className={COLUMN}>{children}</div>
    </div>
  );
}

/**
 * Edges, from the first screen wider than a phone. A single screen of a task is phone width
 * wherever it is read, so on a laptop it is a sheet of paper on a desk — and without a boundary the
 * canvas simply bleeds into the band, which reads as a layout that failed to load. The shadow is
 * lateral and barely there: the brand kit spends its only shadow on the home screen's anchor card,
 * and this is desktop framing the kit has no opinion about, so it borrows the tint rather than the
 * value.
 */
const COLUMN =
  'mx-auto flex min-h-dvh w-full max-w-screen-sm flex-col bg-canvas sm:border-x sm:border-border sm:shadow-[0_0_48px_-16px_rgb(15_15_15/0.22)]';

/**
 * What fills the desk band. A strip of empty colour beside the app looks like something missing;
 * the client's mark and the name of the conference make it look intended, and they are the two
 * things a laptop screen has the room to say twice.
 *
 * Hidden below 1280px, where the band is too narrow to hold this without crowding the app, and
 * `aria-hidden` because every word of it is already on the screen beside it — a screen reader
 * announcing the conference name twice before the first heading is worse than silence.
 */
function DeskBrand({ event, withLogo }: { event: PublicEvent | undefined; withLogo: boolean }) {
  const logo = assetUrl(event?.branding.logoAssetId);

  return (
    <aside
      aria-hidden
      className="sticky top-0 hidden max-w-[17rem] flex-col gap-3 justify-self-center px-6 py-12 xl:flex"
    >
      {withLogo && (
        <img
          src={logo ?? '/logo-lockup.png'}
          alt=""
          className="h-auto w-[132px] max-w-full object-contain"
          // Stated only for the bundled lockup, like the header's: an uploaded logo is whatever
          // shape the organizer sent.
          width={logo ? undefined : 400}
          height={logo ? undefined : 275}
        />
      )}
      <p className="font-display text-card-title font-semibold text-ink">
        {event?.name ?? 'Go For The Greens'}
      </p>
      <p className="text-subtitle text-ink-3">
        The companion is built for the phone in your pocket, which is where it will be all
        conference. Everything is here too, and everything you save on one is on the other.
      </p>
    </aside>
  );
}
